/**
 * Maps a set of device-start failures (audio_input / video_input) to a
 * cause-specific title, summary prose, and bulleted remediation list for the
 * access-error surface. Speaker (audio_output) failures are non-blocking and
 * handled separately as a toast — this helper only handles input devices.
 *
 * Title aggregation is a 2D map of (deviceSet, errorNameSet) → title:
 * single-device single-cause → cause-specific copy; mixed devices, mixed
 * causes, or unrecognized causes → fall-through generic copy.
 *
 * Causes are always curated, actionable strings — never raw SDK error
 * messages. Technical strings like "MeetingManager failed to select video
 * input device. Error fetching device." are diagnostic-only and live in the
 * `device_start_failed` / `device_start_fallback_failed` telemetry events;
 * users see only human-readable guidance.
 *
 * Cause inference is two-tier: trust `error_name` when it's a recognized
 * DOMException name, otherwise pattern-match the trailing portion of
 * `error_message` (which is the underlying browser message that the Chime
 * SDK appends to its own wrapper). Necessary because some browser/SDK
 * combinations (notably Firefox) drop the original `error.name` during
 * wrapping and leave only the textual cue in the message.
 */

export type DeviceStartFailure = {
  device_type: "audio_input" | "video_input";
  error_name?: string;
  error_message?: string;
};

export type DeviceStartGuidance = {
  title: string;
  summary: string;
  causes: string[];
};

const READABLE_CAUSES = [
  "Quit other apps that might be using your camera or microphone (Zoom, FaceTime, OBS, etc.)",
  "If you have a virtual camera or mic driver installed (e.g. Snap Camera, BlackHole), make sure it's running",
  "On Mac, try restarting your browser",
];

const ALLOWED_CAUSES = [
  "Click the camera icon in your browser's address bar and choose Allow, then reload",
  "On Mac: System Settings → Privacy & Security → Camera/Microphone → enable for this browser",
  "On Windows: Settings → Privacy → Camera/Microphone → enable for this browser",
];

const NOT_FOUND_CAUSES = [
  "On Mac: System Settings → Privacy & Security → Camera/Microphone → enable for this browser, then quit and reopen the browser",
  "On Windows: Settings → Privacy → Camera/Microphone → enable for this browser",
  "Make sure your camera is plugged in and not disconnected",
  "If the issue continues, try a different browser",
];

const GENERIC_CAUSES = [
  "Check your browser's site permissions (icon in the address bar) and the Camera/Microphone permission for your browser in your OS settings",
  "Make sure your camera is connected and not in use by another app",
  "Refresh the page and try again",
  "If the issue continues, try a different browser",
];

const OVERCONSTRAINED_CAUSES = [
  "Try a different camera if one is available — some webcams can't deliver 720p video.",
  "Try a different browser. Browser-specific quirks can over-constrain camera requests.",
  "If you're using a virtual camera (OBS, etc.), make sure its source is outputting at least 720p.",
  "You can continue without video — others will still see your name.",
];

type DeviceLabel = "camera" | "microphone" | "camera and microphone";

function deviceLabel(failures: DeviceStartFailure[]): DeviceLabel {
  const devices = new Set(failures.map((f) => f.device_type));
  if (devices.has("video_input") && devices.has("audio_input")) {
    return "camera and microphone";
  }
  if (devices.has("video_input")) return "camera";
  return "microphone";
}

/**
 * Recover the underlying DOMException name from a possibly-wrapped failure.
 *
 * The Chime SDK wraps `getUserMedia` errors with its own message prefix
 * ("MeetingManager failed to select video input device. " + original
 * message). Some browser/SDK combinations preserve the original
 * `error.name` and some don't. When `error_name` is absent or generic
 * (`"Error"`), fall back to substring matching on the appended browser
 * message — those textual cues are stable across browsers because they're
 * standardized DOMException messages.
 */
function inferErrorName(failure: DeviceStartFailure): string | undefined {
  const known = new Set([
    "NotReadableError",
    "NotAllowedError",
    "OverconstrainedError",
    "NotFoundError",
    "AbortError",
  ]);
  if (failure.error_name && known.has(failure.error_name)) {
    return failure.error_name;
  }
  const msg = (failure.error_message ?? "").toLowerCase();
  // Busy / hardware-unreachable cues — Chrome, Edge, Firefox, Safari all
  // surface variants of these in NotReadableError.message.
  if (
    /error fetching device|could not start (video|audio) source|device in use|trackstart|hardware/.test(
      msg
    )
  ) {
    return "NotReadableError";
  }
  // Permission cues — NotAllowedError.message variants.
  if (
    /permission denied|user denied|notallowed|the request is not allowed/.test(
      msg
    )
  ) {
    return "NotAllowedError";
  }
  // No-device cues — NotFoundError.message variants. Firefox's "The object
  // can not be found here" is the canonical case when the OS-level Privacy
  // permission is revoked: Firefox can't see any camera at all and reports
  // it as a missing device rather than a denied permission.
  if (
    /the object can ?not be found|requested device not found|no devices found|object cannot be found/.test(
      msg
    )
  ) {
    return "NotFoundError";
  }
  return undefined;
}

export function getDeviceStartErrorGuidance(
  failures: DeviceStartFailure[]
): DeviceStartGuidance {
  const inferredNames = failures.map((f) => inferErrorName(f));
  const distinctNames = new Set(
    inferredNames.filter((n): n is string => Boolean(n))
  );
  // Only treat as cause-specific when ALL failures share the same inferred
  // cause AND inference succeeded for every failure. Any unmapped failure
  // forces fall-through.
  const onlyName =
    distinctNames.size === 1 && inferredNames.every(Boolean)
      ? [...distinctNames][0]
      : undefined;
  const label = deviceLabel(failures);

  if (onlyName === "NotReadableError") {
    const titles: Record<DeviceLabel, string> = {
      camera: "Your camera is busy",
      microphone: "Your microphone is busy",
      "camera and microphone": "Your camera and microphone are busy",
    };
    return {
      title: titles[label],
      summary: `We couldn't start your ${label}. Try the steps below.`,
      causes: READABLE_CAUSES,
    };
  }

  if (onlyName === "NotAllowedError") {
    const titles: Record<DeviceLabel, string> = {
      camera: "Camera permission needed",
      microphone: "Microphone permission needed",
      "camera and microphone": "Camera and microphone permission needed",
    };
    return {
      title: titles[label],
      summary: `We couldn't start your ${label}. Try the steps below.`,
      causes: ALLOWED_CAUSES,
    };
  }

  if (onlyName === "NotFoundError") {
    const titles: Record<DeviceLabel, string> = {
      camera: "Couldn't find your camera",
      microphone: "Couldn't find your microphone",
      "camera and microphone": "Couldn't find your camera or microphone",
    };
    return {
      title: titles[label],
      summary: `We couldn't find your ${label}. Try the steps below.`,
      causes: NOT_FOUND_CAUSES,
    };
  }

  if (onlyName === "OverconstrainedError") {
    const titles: Record<DeviceLabel, string> = {
      camera: "Your camera couldn't deliver the requested quality",
      microphone: "Your microphone couldn't deliver the requested quality",
      "camera and microphone":
        "Your camera and microphone couldn't deliver the requested quality",
    };
    return {
      title: titles[label],
      summary: `Your ${label} doesn't support the quality we asked for. Try the steps below.`,
      causes: OVERCONSTRAINED_CAUSES,
    };
  }

  const titles: Record<DeviceLabel, string> = {
    camera: "Couldn't start your camera",
    microphone: "Couldn't start your microphone",
    "camera and microphone": "Couldn't start your camera and microphone",
  };
  return {
    title: titles[label],
    summary: `We couldn't start your ${label}. Try the steps below.`,
    causes: GENERIC_CAUSES,
  };
}
